import{ useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import Label from "../../../../../components/form_fields/Label";
import OrangeButton from "../../../../../components/buttons/OrangeButton";
import { AiOutlineDownCircle } from "react-icons/ai";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import FormDescriptionInput from "../../../../../components/form_fields/FormDescriptionInput";
import { useDeleteCostCentre, useUpdateCostCenter } from "../../../../../services/cost-center.service";
import { CostCenterDto } from "../../../../../models/cost-center.dto";
import DeleteModal from "../../../../../components/modals/DeleteModal";
import { useManageAccordionsData } from "../../../../../helpers/useManageAccordions";
import FormCodeInput from "../../../../../components/form_fields/FormCodeInput";
import { PermissionChecker, PERMISSIONS } from "../../../../../helpers/usePermissionManager";

interface IFormInputs {
	name: string;
	code: string;
	description: string;
    sequenceNumber: number;
}

interface IEditCostCenter {
	costCenter: CostCenterDto;
}

const EditCostCenter = ({ costCenter }: IEditCostCenter) => {
    const [expanded, setExpanded] = useState(false);
	const [formDisabled, setFormDisabled] = useState(true);
	const { mutate: updateCostCenter } = useUpdateCostCenter();
	const { reloadAccordionsData } = useManageAccordionsData();
	const [uploading, setUploading] = useState(false);
	const [openDeleteModal, setOpenDeleteModal] = useState(false);

	const {
		register,
		handleSubmit,
		reset,
		formState: { errors },
	} = useForm<IFormInputs>({
		defaultValues: {
			name: costCenter.name,
			code: costCenter.code,
			description: costCenter.description,
			sequenceNumber: costCenter.sequenceNumber as number,
		}
	});

	const onSubmit: SubmitHandler<IFormInputs> = async (data: IFormInputs) => {
		setUploading(true);

		updateCostCenter(
			{ payload: data, id: costCenter.id }, 
			{
				onSuccess: () => {
					reloadAccordionsData();
					setFormDisabled(true);
					setExpanded(false);
				},
				onSettled: () => setUploading(false),
			}
		)
	};
	
	const [deleting, setDeleting] = useState(false);
	const { mutate: deleteCostCenter } = useDeleteCostCentre();

	const handleDeleteMethod = () => {
		setDeleting(true);

		deleteCostCenter(
			{ id: costCenter?.id },
			{
				onSuccess: () => {
					reloadAccordionsData();
					setOpenDeleteModal(false);
				},
				onSettled: () => setDeleting(false),
			}
		);
	};

	return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(prev => !prev)}
            classes={{
                root: "border border-gray-300 rounded-lg py-2 mb-2",
            }}
        >
			<AccordionSummary
				expandIcon={<AiOutlineDownCircle className="text-xl" />}
				aria-controls={costCenter.id + "bh-content"}
				id={costCenter.id + "bh-header"}
				sx={{ px: 3, border: "1px" }}
			>
				<h2 className="font-inter text-sm font-semibold">
					{costCenter.name}
				</h2>
			</AccordionSummary>
			<AccordionDetails sx={{ px: 3 }}>
				<form onSubmit={handleSubmit(onSubmit)}>
					<>
						<div className="border-b-[1.5px] pb-3 flex justify-between font-semibold text-2xs">
							<h2 className="font-semibold text-2xs">Details</h2>
							<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
								<button
									type="button"
									className={`border-none text-primary-blue text-2xs ${
										!formDisabled ? "hidden" : ""
									}`}
									onClick={() => setFormDisabled(false)}
								>
									Edit
								</button>
							</PermissionChecker>
						</div>
						<div className="grid grid-cols-2 gap-x-4 gap-y-6 mt-4 ">
							<div className="w-full">
								<Label title="Name" for="name" />
								<input
									defaultValue={costCenter.name}
									disabled={formDisabled}
									placeholder={"Enter name"}
									type="text"
									{...register("name", {
										required: "Enter name here",
									})}
									id="name"
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors.name
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors.name && (
									<p className="text-red-500 text-sm mt-1">{errors.name.message}</p>
								)}
							</div>
							<div className="w-full">
								<Label title="Unique Code" for="code" />
								<FormCodeInput
									register={register}
									attributes={{
										defaultValue: costCenter.code,
										disabled: formDisabled,
										className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
											errors.code
												? " border border-red-500 focus:border-red-500 focus:outline-red-500"
												: "border-none"
										}`,
									}}
								/>
								{errors.code && (
									<p className="text-red-500 text-sm mt-1">
										{errors.code.message}
									</p>
								)}
							</div>
							<div className="w-full col-span-2">
								<Label title="Description" for="description" optional />
								<FormDescriptionInput
									required={false}
									attributes={{
										defaultValue: costCenter.description,
										className: `w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm ${
											errors.description
												? " border border-red-500 focus:border-red-500 focus:outline-red-500"
												: "border-none"
										}`
									}}
									register={register}
									disabled={formDisabled}
								/>
							</div>
							<div className="w-full">
								<Label title="Sequence Number" for={`sequenceNumber`} />
								<input
									defaultValue={costCenter.sequenceNumber as number}
									disabled={formDisabled}
									placeholder={"Enter sequence number"}
									type="number"
									{...register(`sequenceNumber`, {
										required: "Enter sequence number here",
										valueAsNumber: true
									})}
									id={`sequenceNumber`}
									className={`w-full bg-secondary-gray rounded-lg h-14 px-5 mt-2 text-sm  ${
										errors?.sequenceNumber
											? " border border-red-500 focus:border-red-500 focus:outline-red-500"
											: "border-none"
									}`}
								/>
								{errors?.sequenceNumber && (
									<p className="text-red-500 text-sm mt-1">
										{errors?.sequenceNumber?.message}
									</p>
								)}
							</div>
						</div>
					</>
					
					{!formDisabled && (
						<div className="flex w-full justify-end space-x-4 items-center mt-10">
							<button
								type="button"
								className="px-12 py-4 border text-gray-400 hover:text-gray-500 border-gray-300 rounded-lg text-2xs"
								onClick={() => {
									setFormDisabled(true);
									reset();
								}}
							>
								Cancel
							</button>
							<OrangeButton
								title={`${uploading ? "Saving..." : "Save"}`}
								className={"px-12"}
								type={"submit"}
								disabled={uploading}
							/>
						</div>
					)}
					
					{formDisabled && (
						<div className="flex w-full justify-end space-x-4 items-center mt-10">
							<PermissionChecker permissions={[PERMISSIONS.COMPANY_CONFIGS_WRITE]}>
								<button
									type="button"
									onClick={() => setOpenDeleteModal(true)}
									className={`px-12 py-4 border text-white hover:shadow-sm bg-secondary-red rounded-lg text-2xs ${uploading && "opacity-50"}`}
									disabled={uploading}
								>
									Delete
								</button>
							</PermissionChecker>
						</div>
					)}

					<DeleteModal
						openDeleteModal={openDeleteModal}
						spanContent={costCenter.name}
						setOpenDeleteModal={setOpenDeleteModal}
						handleDeleteMethod={handleDeleteMethod}
						deleting={deleting}
					/>
				</form>
			</AccordionDetails>
		</Accordion>
	);
};

export default EditCostCenter;
